import { useEffect, useRef, useState } from 'react';
import { Loader, Table, Group } from '@mantine/core';
import Header from '../../components/util/header';
import { useSelector } from 'react-redux';
import {
  fetchTransactionLog,
  fetchTransactionList,
  fetchTransactionReport,
  fetchTransactionXLS,
  Transaction
} from '../../../libraries/transactions';
import { AppState } from '../../client-redux/reducers';
import { LoginState } from '../../client-redux/login/reducer';
import { Language } from '../../languages/languageHandler';
import {
  formatCurrAmount,
  formatDocTypeName,
  formatShortTransactionType,
  formatTransactionMethod
} from '../../utils/script';
import PaginationComponent from '../../components/pagination';
import { ICurrency } from '../../../libraries/currencies';
import { ILocation } from '../../../libraries/location';
import { useFitTableRows } from '../../components/util/fit-table-rows-hook';
import FilterBar, { FilterBarFieldsData } from '../../components/filterbar';
import { formatDateTimeNSecLabel, formatDateToISO } from '../../utils/date';
import ShowTransaction from './show-transactions';
import { useParams } from 'react-router-dom';
import { isErrorResponse, isZarphSuccessResponse, useStatusOperDialog } from '../../context/status-oper-dialog';
import './styles.scss';

const hideColumns = {
  method: false,
  amountReceived: false,
  amountDispensed: false,
  doc: false,
  card: true,
  account: true,
  depositor: true,
  reason: true,
  declaration: true,
  balance: true
};

const Transactions = () => {
  const lang = useSelector<AppState, Language>(state => state.lang.lang);
  const login = useSelector<AppState, LoginState>(state => state.login);
  const region = useSelector<AppState, string | undefined>(state => state.login.user.region);
  const locations = useSelector<AppState, ILocation[]>(state => state.locations.locations);
  const currencies = useSelector<AppState, ICurrency[]>(state => state.currencies.currencies);
  const controller = useRef(new AbortController()).current;

  const [openDetails, setOpenDetails] = useState<boolean>(false);
  const [, setActiveRow] = useState<number>();
  const [selectedTransaction, setSelectedTransaction] = useState<Transaction>();
  const [selectedTransactionCurrency, setSelectedTransactionCurrency] = useState<ICurrency>();
  const [loading, setLoading] = useState(false);
  const [totalRows, setTotalRows] = useState<number>(0);
  const [offset, setOffset] = useState<number>(0);
  const {
    limit = 10,
    setLimit,
    tableRef
  } = useFitTableRows({
    bufferRows: -2,
    minHeightForDynamic: 400
  });
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const tableWrapperRef = useRef<HTMLDivElement>(null);

  const { entid, uid } = useParams();
  const [filterBarData, setFilterBarData] = useState<FilterBarFieldsData>(() => ({
    entity: entid ? Number(entid) : 0,
    uid: uid ? Number(uid) : undefined,
    lid: 0,
    eid: 0,
    period: 0,
    beginDateTime: undefined,
    endDateTime: undefined,
    noteSN: undefined,
    transactionId: undefined,
    financialOnly: false,
    docCode: undefined
  }));

  const isDepositMachine = login.equipmentTypes ? login.equipmentTypes.some(e => Math.floor(e / 100) === 4) : false;
  const isPaymentMachine = login.equipmentTypes ? login.equipmentTypes.some(e => Math.floor(e / 100) !== 4) : false;

  const { handleApiError } = useStatusOperDialog();

  useEffect(() => {
    if (isDepositMachine && !isPaymentMachine) {
      hideColumns.method = true;
      hideColumns.amountReceived = true;
      hideColumns.amountDispensed = true;
      hideColumns.doc = true;
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (filterBarData && limit) {
      loadTransactionList();
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterBarData, limit, offset]);

  const handleFilterBarOnDataChange = (data: FilterBarFieldsData) => {
    setFilterBarData(data);
    changePage(1);
  };

  useEffect(() => {
    if (entid || uid) {
      // update only the fields coming from the URL
      setFilterBarData(prev => ({
        ...prev,
        entity: entid ? Number(entid) : prev.entity,
        uid: uid ? Number(uid) : prev.uid
      }));
    }
  }, [entid, uid]);

  // -------------------- Transactions.tsx --------------------

  const loadTransactionList = async () => {
    if (!filterBarData || !limit) return;

    setLoading(true);
    setTransactions([]);
    setTotalRows(0);

    fetchTransactionList(
      controller,
      filterBarData.entity || 0,
      filterBarData.lid || 0,
      filterBarData.eid || 0,
      filterBarData.period || 0,
      offset,
      limit,
      true,
      filterBarData.noteSN,
      filterBarData.transactionId,
      formatDateToISO(filterBarData.beginDateTime),
      formatDateToISO(filterBarData.endDateTime),
      filterBarData.uid,
      undefined,
      filterBarData.financialOnly,
      filterBarData.docCode
    )
      .then(response => {
        if (isZarphSuccessResponse(response, [9004])) {
          setTransactions(response?.data?.transactions || []);
          setTotalRows(response?.data?.totalRows || 0);
        } else {
          console.error('Error response:', response);
          handleApiError(response, lang.ALERT_ERROR_FETCHING_DATA);
          setLoading(false);
        }
      })
      .catch(err => {
        // unexpected errors go here
        handleApiError(err, lang.ALERT_ERROR_FETCHING_DATA);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  // ----------------- Modal information with logs -------------------//
  const showTransactionDetails = (n: number) => {
    if (transactions.length) {
      setActiveRow(n);
      const transaction = transactions[n];
      setSelectedTransaction(transaction);
      if (currencies) {
        const matchingCurrency = currencies.find(c => c.cid === transaction.cid);
        setSelectedTransactionCurrency(matchingCurrency);
      }
      setOpenDetails(true); // open only after selection
    }
  };

  const getLog = async () => {
    if (!selectedTransaction || !transactions.length) return;

    try {
      const response = await fetchTransactionLog(
        controller,
        selectedTransaction.eid,
        selectedTransaction.period,
        selectedTransaction.code
      );

      if (isErrorResponse(response) || response?.data.statusOper?.code !== 0) {
        handleApiError(response, lang.GLOBAL_ERROR);
        return;
      }
    } catch (err: any) {
      handleApiError(err, lang.GLOBAL_ERROR);
    }
  };

  const getReport = async () => {
    if (!selectedTransaction || !transactions.length) return; // skip
    try {
      const response = await fetchTransactionReport(
        controller,
        selectedTransaction.eid,
        selectedTransaction.period,
        selectedTransaction.code
      );
      if (isErrorResponse(response) || response?.data.statusOper?.code !== 0) {
        handleApiError(response, lang.GLOBAL_ERROR);
        return;
      }
    } catch (err: any) {
      handleApiError(err, lang.TRANSACTION_REPORT_ERROR);
    }
  };

  const getXLSReport = async () => {
    if (!filterBarData) return;

    try {
      const response = await fetchTransactionXLS(
        filterBarData.entity || 0,
        filterBarData.lid || 0,
        filterBarData.eid || 0,
        controller,
        formatDateToISO(filterBarData.beginDateTime),
        formatDateToISO(filterBarData.endDateTime),
        filterBarData.uid,
        filterBarData.financialOnly
      );
      if (response?.data.statusOper?.code !== 0) {
        handleApiError(response, lang.GLOBAL_ERROR);
      }
    } catch (error) {
      handleApiError(error, lang.GLOBAL_ERROR);
    }
  };

  //----------------------------------------------------------------//

  const changePage = (page: number) => {
    const newOffset = (page - 1) * limit;
    setCurrentPage(page);
    setOffset(newOffset);
  };

  const resultsSelect = (newValue: number) => {
    const newLimit = Number(newValue);
    setLimit(newLimit);
    setOffset(0);
    setCurrentPage(1);
  };

  const formatMethodColumn = (lang: Language, transaction: Transaction) => {
    if (transaction.type.trim().startsWith('010101')) {
      return formatTransactionMethod(lang, transaction.valueType);
    }
    return '-';
  };

  return (
    <div className="transactions">
      {openDetails && selectedTransaction && selectedTransactionCurrency ? (
        <ShowTransaction
          transaction={selectedTransaction}
          openDetails={openDetails}
          hideBtn={() => setOpenDetails(false)}
          lang={lang}
          currency={selectedTransactionCurrency}
          getLog={async () => getLog()}
          getReport={async () => getReport()}
        />
      ) : null}

      <Header pageTitle={lang.TRANSACTIONS} />

      <FilterBar
        initialData={filterBarData}
        onDataChange={handleFilterBarOnDataChange}
        onSearch={loadTransactionList}
        fieldsUsage={{
          entity: true,
          lid: true,
          eid: true,
          uid: true,
          period: true,
          beginDateTime: true,
          endDateTime: true,
          noteSN: true,
          transactionId: true,
          financialOnly: true,
          docCode: true
        }}
        xlsReport={transactions.length ? getXLSReport : undefined}
      />

      <div ref={tableWrapperRef}>
        <Table.ScrollContainer minWidth={500} ref={tableRef}>
          <Table striped highlightOnHover verticalSpacing="sm">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{lang.EQUIPMENT_LOCAL}</Table.Th>
                <Table.Th>{lang.EQUIPMENT_ID_MIN}</Table.Th>
                <Table.Th>{lang.PROCESSED_GLASS_PERIOD}</Table.Th>
                <Table.Th>{lang.GLOBAL_CODE}</Table.Th>
                <Table.Th>{lang.TRANSACTION_USER_CASHIER}</Table.Th>
                <Table.Th>{lang.GLOBAL_DATE_TIME}</Table.Th>
                <Table.Th>{lang.GLOBAL_TYPE}</Table.Th>
                {!hideColumns.method && <Table.Th>{lang.GLOBAL_METHOD}</Table.Th>}
                <Table.Th>{lang.GLOBAL_AMOUNT}</Table.Th>
                {!hideColumns.amountReceived && <Table.Th>{lang.TRANSACTION_RECEIVED}</Table.Th>}
                {!hideColumns.amountDispensed && <Table.Th>{lang.TRANSACTION_DISPENSED}</Table.Th>}
                {!hideColumns.doc && <Table.Th>{lang.TRANSACTION_DOC}</Table.Th>}
                {!hideColumns.card && <Table.Th>{lang.USER_CARD}</Table.Th>}
                {!hideColumns.account && <Table.Th>{lang.GLOBAL_ACCOUNT}</Table.Th>}
                {!hideColumns.depositor && <Table.Th>{lang.TRANSACTION_DEPOSITOR}</Table.Th>}
                {!hideColumns.reason && <Table.Th>{lang.GLOBAL_REASON}</Table.Th>}
                {!hideColumns.declaration && <Table.Th>{lang.GLOBAL_DECLARATION}</Table.Th>}
                {!hideColumns.balance && <Table.Th>{lang.GLOBAL_BALANCE}</Table.Th>}
              </Table.Tr>
            </Table.Thead>

            <Table.Tbody>
              {loading ? (
                <Table.Tr>
                  <Table.Td colSpan={16}>
                    <Loader color="#016273" />
                  </Table.Td>
                </Table.Tr>
              ) : (
                <>
                  {!transactions.length ? (
                    <Table.Tr>
                      <Table.Td colSpan={16} style={{ textAlign: 'center' }}>
                        {lang.GLOBAL_NO_RESULTS}
                      </Table.Td>
                    </Table.Tr>
                  ) : (
                    transactions.map((transaction, index) => {
                      const matchingCurrency = currencies?.find(c => c.cid === transaction?.cid);
                      const matchingLocation = locations.find(shop => shop.sid === transaction.sid);
                      return (
                        <Table.Tr
                          key={index}
                          style={{ cursor: 'pointer' }}
                          onClick={() => showTransactionDetails(index)}
                        >
                          <Table.Td>{matchingLocation ? matchingLocation.name : ''}</Table.Td>
                          <Table.Td>{transaction.eid}</Table.Td>
                          <Table.Td>{transaction.period}</Table.Td>
                          <Table.Td>{transaction.code}</Table.Td>
                          <Table.Td>
                            {transaction.uid ? `${transaction.user.uid} - ${transaction.user.name}` : ''}
                          </Table.Td>
                          <Table.Td>{formatDateTimeNSecLabel(transaction.dateTime, region)}</Table.Td>
                          <Table.Td>{formatShortTransactionType(lang, transaction.type)}</Table.Td>
                          {!hideColumns.method && <Table.Td>{formatMethodColumn(lang, transaction)}</Table.Td>}
                          <Table.Td>
                            {matchingCurrency ? formatCurrAmount(transaction.value || 0, matchingCurrency) : '-'}
                          </Table.Td>
                          {!hideColumns.amountReceived && (
                            <Table.Td>
                              {matchingCurrency ? formatCurrAmount(transaction.valueRec || 0, matchingCurrency) : '-'}
                            </Table.Td>
                          )}
                          {!hideColumns.amountDispensed && (
                            <Table.Td>
                              {matchingCurrency ? formatCurrAmount(transaction.valueDis || 0, matchingCurrency) : '-'}
                            </Table.Td>
                          )}
                          {!hideColumns.doc && (
                            <Table.Td>
                              {transaction.docCode
                                ? `${formatDocTypeName(lang, transaction.docType)}/${transaction.docCode}`
                                : ''}
                            </Table.Td>
                          )}
                          {!hideColumns.card && <Table.Td>{transaction.bankingDetails?.card}</Table.Td>}
                          {!hideColumns.account && <Table.Td>{transaction.bankingDetails?.account}</Table.Td>}
                          {!hideColumns.declaration && <Table.Td>{transaction.bankingDetails?.declaration}</Table.Td>}
                          {!hideColumns.depositor && <Table.Td>{transaction.bankingDetails?.depositor}</Table.Td>}
                          {!hideColumns.reason && <Table.Td>{transaction.bankingDetails?.reason}</Table.Td>}
                          {!hideColumns.balance && (
                            <Table.Td>
                              {matchingCurrency ? formatCurrAmount(transaction.balance || 0, matchingCurrency) : '-'}
                            </Table.Td>
                          )}
                        </Table.Tr>
                      );
                    })
                  )}
                </>
              )}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      </div>

      <Group>
        <PaginationComponent
          total={totalRows}
          itemsPerPage={limit}
          dynamicLimit={limit}
          currentPage={currentPage}
          onPageChange={changePage}
          onResultsChange={resultsSelect}
          offset={offset}
        />
      </Group>
    </div>
  );
};

export default Transactions;
